import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from '../../../app/modules/auth';
import { getSupplierInfo } from '../../../app/modules/my-profile/core/_requests'; // Removed getSupplierContractInfo
import axios, { AxiosError } from 'axios';

interface SupplierInfo {
  supplierId: number | null;
  supplierName: string;
}

const SupplierInfoContext = createContext<SupplierInfo | undefined>(undefined);

export const useSupplierInfo = () => {
  const context = useContext(SupplierInfoContext);
  if (context === undefined) {
    throw new Error('useSupplierInfo must be used within a SupplierInfoProvider');
  }
  return context;
};

interface SupplierInfoProviderProps {
  children: ReactNode;
}

export const SupplierInfoProvider: React.FC<SupplierInfoProviderProps> = ({ children }) => {
  const { currentUser } = useAuth();
  const [supplierId, setSupplierId] = useState<number | null>(null);
  const [supplierName, setSupplierName] = useState<string>('User'); // Default or loading state

  useEffect(() => {
    const fetchSupplierData = async () => {


      if (!currentUser?.token) {
        setSupplierId(null);
        setSupplierName('User');
        return;
      }

      try {
        const { data: infoData } = await getSupplierInfo(currentUser.token, 'en');
        const supplierInfo = infoData?.supplier_info;
        
        // Set Supplier Name
        if (supplierInfo?.name) {
          const firstName = supplierInfo.name.split(' ')[0];
          setSupplierName(firstName);
        } else {
          setSupplierName('User'); // Fallback
          console.error('[SupplierInfoProvider] Supplier name not found in infoData. Response:', infoData);
        }

        // Set Supplier ID from supplierInfo.id
        if (supplierInfo?.id !== undefined && supplierInfo?.id !== null) {
          const id = Number(supplierInfo.id);
          if (!isNaN(id)) {
            setSupplierId(id);
          } else {
            setSupplierId(null);
            console.error('[SupplierInfoProvider] Supplier ID from supplierInfo.id is not a valid number:', supplierInfo.id, '. Response:', infoData);
          }
        } else {
          setSupplierId(null);
          console.error('[SupplierInfoProvider] Supplier ID (supplierInfo.id) not found in infoData. Response:', infoData);
        }

      } catch (error) {
        console.error('[SupplierInfoProvider] Failed to fetch supplier data from getSupplierInfo:', error);
        // Check if the error is an Axios error and log more details
        if (axios.isAxiosError(error)) {
          const axiosError = error as AxiosError;
          console.error('[SupplierInfoProvider] Axios error response:', axiosError.response?.data);
          console.error('[SupplierInfoProvider] Axios error status:', axiosError.response?.status);
        } else if (error instanceof Error) {
          console.error('[SupplierInfoProvider] Generic error message:', error.message);
        } else {
          console.error('[SupplierInfoProvider] Unknown error type:', error);
        }
        setSupplierId(null);
        setSupplierName('User'); // Reset name on error as well
      }
    };

    fetchSupplierData();
  }, [currentUser, currentUser?.token]);

  return (
    <SupplierInfoContext.Provider value={{ supplierId, supplierName }}>
      {children}
    </SupplierInfoContext.Provider>
  );
};
